import { useState } from 'react';
import { motion } from 'motion/react';
import { Wrench, Plus, Pencil, Trash2, Search } from 'lucide-react';
import { useApp } from '@/store';
import { SparePartType, type SparePart } from '@/data/types';
import { useT } from '@/lib/i18n';
import { fmtCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

type Form = {
  articleNr: string;
  name: string;
  type: SparePartType;
  price: string;
};
const blank = (): Form => ({
  articleNr: '',
  name: '',
  type: SparePartType.EXTINGUISHER,
  price: '0',
});
const fromPart = (p: SparePart): Form => ({
  articleNr: p.articleNr,
  name: p.name,
  type: p.type,
  price: String(p.price),
});

export function SpareParts() {
  const t = useT();
  const parts = useApp(s => s.spareParts);
  const settings = useApp(s => s.settings);
  const upsertSparePart = useApp(s => s.upsertSparePart);
  const deleteSparePart = useApp(s => s.deleteSparePart);
  const toast = useApp(s => s.toast);

  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'all' | 'extinguisher' | 'hose'>('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SparePart | null>(null);
  const [form, setForm] = useState<Form>(blank());

  const filtered = parts.filter(p => {
    if (tab !== 'all' && p.type !== tab) return false;
    if (
      q &&
      !p.name.toLowerCase().includes(q.toLowerCase()) &&
      !p.articleNr.includes(q)
    )
      return false;
    return true;
  });

  const openCreate = () => {
    setEditing(null);
    setForm(blank());
    setOpen(true);
  };
  const openEdit = (p: SparePart) => {
    setEditing(p);
    setForm(fromPart(p));
    setOpen(true);
  };
  const handleSave = () => {
    if (!form.articleNr.trim() || !form.name.trim()) return;
    upsertSparePart({
      articleNr: form.articleNr.trim(),
      name: form.name.trim(),
      type: form.type,
      price: parseFloat(form.price) || 0,
    });
    toast(editing ? t('common.save') : t('parts.newPart'));
    setOpen(false);
  };
  const handleDelete = (p: SparePart) => {
    if (!confirm(`${t('common.delete')} ${p.name}?`)) return;
    deleteSparePart(p.articleNr);
    toast(t('common.delete'));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Wrench className="h-6 w-6 text-primary" /> {t('parts.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('parts.subtitle')}</p>
        </div>
        <Button size="sm" onClick={openCreate} data-focus="parts-new">
          <Plus className="h-4 w-4" />
          {t('parts.newPart')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <Tabs value={tab} onValueChange={v => setTab(v as typeof tab)}>
              <TabsList>
                <TabsTrigger value="all">
                  {t('common.all')} ({parts.length})
                </TabsTrigger>
                <TabsTrigger value="extinguisher">
                  {t('parts.extinguisher')}
                </TabsTrigger>
                <TabsTrigger value="hose">{t('parts.hose')}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder={t('parts.searchPlaceholder')}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('table.articleNr')}</TableHead>
                <TableHead>{t('table.name')}</TableHead>
                <TableHead>{t('table.type')}</TableHead>
                <TableHead className="text-right">{t('table.price')}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(p => (
                <TableRow key={p.articleNr}>
                  <TableCell className="font-mono text-xs">
                    {p.articleNr}
                  </TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {p.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {fmtCurrency(p.price, settings.currency)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 sm:h-7 sm:w-7"
                        onClick={() => openEdit(p)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 sm:h-7 sm:w-7"
                        onClick={() => handleDelete(p)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? t('common.edit') : t('parts.newPart')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            <div>
              <Label>{t('table.articleNr')}</Label>
              <Input
                value={form.articleNr}
                onChange={e =>
                  setForm(f => ({ ...f, articleNr: e.target.value }))
                }
                disabled={!!editing}
                className="font-mono"
              />
            </div>
            <div>
              <Label>{t('table.type')}</Label>
              <Select
                value={form.type}
                onValueChange={v =>
                  setForm(f => ({ ...f, type: v as SparePartType }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SparePartType.EXTINGUISHER}>
                    {t('parts.extinguisher')}
                  </SelectItem>
                  <SelectItem value={SparePartType.HOSE}>
                    {t('parts.hose')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>{t('table.name')}</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('table.price')}</Label>
              <Input
                type="number"
                step="0.01"
                value={form.price}
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
